<template>
  <main-template>
    <v-container>
      <v-row>
        <v-col cols="12" class="d-flex align-center">
          <v-btn color="primary" x-small text @click="$router.go(-1)">
            <v-icon left>fa fa-angle-left</v-icon>
          </v-btn>
          <span class="text-h6 ml-2">Detalhes da mensalidade</span>
        </v-col>
      </v-row>

      <v-row justify="center">
        <v-col cols="12" sm="8" md="6">
          <div v-if="carregando" class="text-center py-8">
            <v-progress-circular indeterminate color="primary" />
          </div>

          <template v-else-if="parcela">
            <v-card rounded="lg" class="mb-4">
              <v-card-text>
                <div class="text-subtitle-1 font-weight-bold">
                  {{ parcela.descricaoMensalidade }}
                </div>
                <div v-if="parcela.aluno" class="text-body-2 grey--text">
                  {{ parcela.aluno.nomecompleto }}
                </div>
                <div class="text-caption grey--text mt-1">
                  Vence em {{ dataVencimentoFormatada }}
                </div>

                <div class="d-flex flex-wrap mt-2">
                  <v-chip v-if="parcela.estaPaga" x-small color="grey" class="mr-1 mb-1">
                    Paga
                  </v-chip>
                  <template v-else>
                    <v-chip v-if="parcela.estaAtrasada" x-small color="error" class="mr-1 mb-1">
                      Atrasada
                    </v-chip>
                    <v-chip v-else x-small color="info" class="mr-1 mb-1"> A vencer </v-chip>
                    <v-chip v-if="parcela.temDesconto" x-small color="success" class="mr-1 mb-1">
                      Com desconto
                    </v-chip>
                  </template>
                </div>

                <div class="mt-4">
                  <div class="text-caption grey--text">Total a pagar</div>
                  <div class="text-h4 font-weight-bold primary--text">
                    {{ parcela.valorEmAberto | currency }}
                  </div>
                </div>
              </v-card-text>
            </v-card>

            <v-alert
              v-if="proximaTierValida && parcela.estaEmAberto"
              type="info"
              text
              dense
              class="mb-4"
            >
              Pague até
              {{ proximaTierValida.data | dateParse("YYYY-MM-DD") | dateFormat("DD/MM/YYYY") }}
              e garanta o desconto de {{ proximaTierValida.valor | currency }}.
            </v-alert>

            <!-- Breakdown de valores -->
            <v-card outlined rounded="lg" class="mb-6">
              <v-list dense>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="text-body-2">Valor original</v-list-item-title>
                  </v-list-item-content>
                  <span class="text-body-2">{{ parcela.valor | currency }}</span>
                </v-list-item>

                <v-divider />

                <template v-if="parcela.descontosTiers.length > 0">
                  <v-list-item v-for="(tier, i) in parcela.descontosTiers" :key="i">
                    <v-list-item-content>
                      <v-list-item-title
                        :class="
                          tierExpirado(tier)
                            ? 'text-body-2 grey--text text-decoration-line-through'
                            : tier.valor == parcela.descontoAplicavel
                            ? 'text-body-2 font-weight-bold'
                            : 'text-body-2 grey--text'
                        "
                      >
                        Desconto de pontualidade
                      </v-list-item-title>
                      <v-list-item-subtitle
                        :class="
                          tierExpirado(tier)
                            ? 'text-caption grey--text text-decoration-line-through'
                            : 'text-caption'
                        "
                      >
                        Válido até
                        {{ tier.data | dateParse("YYYY-MM-DD") | dateFormat("DD/MM/YYYY") }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <span
                      :class="
                        tierExpirado(tier)
                          ? 'text-body-2 grey--text text-decoration-line-through'
                          : tier.valor == parcela.descontoAplicavel
                          ? 'text-body-2 success--text font-weight-bold'
                          : 'text-body-2 grey--text'
                      "
                    >
                      - {{ tier.valor | currency }}
                    </span>
                  </v-list-item>
                  <v-divider />
                </template>

                <v-list-item v-if="parcela.multaCalculada > 0">
                  <v-list-item-content>
                    <v-list-item-title class="text-body-2">Multa</v-list-item-title>
                  </v-list-item-content>
                  <span class="text-body-2 error--text"
                    >+ {{ parcela.multaCalculada | currency }}</span
                  >
                </v-list-item>

                <v-divider v-if="parcela.multaCalculada > 0" />

                <v-list-item v-if="parcela.jurosCalculados > 0">
                  <v-list-item-content>
                    <v-list-item-title class="text-body-2">Juros mora</v-list-item-title>
                    <v-list-item-subtitle class="text-caption error--text">
                      {{ parcela.diasAtraso }} {{ parcela.diasAtraso === 1 ? "dia" : "dias" }} em
                      atraso
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <span class="text-body-2 error--text"
                    >+ {{ parcela.jurosCalculados | currency }}</span
                  >
                </v-list-item>

                <v-divider v-if="parcela.jurosCalculados > 0" />

                <v-list-item class="font-weight-bold">
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">Valor total</v-list-item-title>
                  </v-list-item-content>
                  <span class="font-weight-bold primary--text">
                    {{ parcela.valorEmAberto | currency }}
                  </span>
                </v-list-item>
              </v-list>
            </v-card>
            <v-btn v-if="parcela.estaEmAberto" color="primary" block @click="finalizarPagamento">
              Pagar
            </v-btn>
          </template>
        </v-col>
      </v-row>
    </v-container>
  </main-template>
</template>

<script>
import { mapGetters } from "vuex";
import Parcela from "@/Models/Parcela";

export default {
  name: "MensalidadeDetalhe",

  computed: {
    ...mapGetters("Mensalidades", ["matriculaSelecionada"]),

    matriculaId() {
      return this.$route.params.matricula_id;
    },

    parcelaId() {
      return this.$route.params.parcela_id;
    },

    proximaTierValida() {
      if (!this.parcela) return null;
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      return (
        this.parcela.descontosTiers
          .filter((t) => new Date(`${t.data}T23:59:59`) >= hoje)
          .sort((a, b) => new Date(a.data) - new Date(b.data))[0] || null
      );
    },

    dataVencimentoFormatada() {
      if (!this.parcela?.referencia_3) return "";
      const meses = [
        "Janeiro",
        "Fevereiro",
        "Março",
        "Abril",
        "Maio",
        "Junho",
        "Julho",
        "Agosto",
        "Setembro",
        "Outubro",
        "Novembro",
        "Dezembro",
      ];
      const partes = this.parcela.referencia_3.split("-");
      const dia = parseInt(partes[2], 10);
      const mes = parseInt(partes[1], 10) - 1;
      const ano = partes[0];
      return `${dia} de ${meses[mes]} de ${ano}`;
    },
  },

  data() {
    return {
      carregando: true,
      parcela: null,
    };
  },

  async mounted() {
    await this.carregarParcela();
  },

  methods: {
    async carregarParcela() {
      this.carregando = true;
      try {
        const matriculaAtualizada = await this.$services.matriculasService.getDetails(
          this.matriculaId
        );
        this.$store.commit("Mensalidades/setMatriculaSelecionada", matriculaAtualizada);

        const aluno = matriculaAtualizada.aluno;
        const turma = matriculaAtualizada.turma;
        const encontrada = matriculaAtualizada.parcelas?.find(
          (p) => String(p.id) === String(this.parcelaId)
        );
        if (encontrada) {
          this.parcela = new Parcela({ ...encontrada, aluno, turma });
          return;
        }
        this.parcela = await this.$services.parcelasService.getParcela(this.parcelaId);
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.carregando = false;
      }
    },

    tierExpirado(tier) {
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      return new Date(`${tier.data}T23:59:59`) < hoje;
    },

    selecionarParcela() {
      this.$store.commit("Mensalidades/setParcelasSelecionadas", [this.parcela]);
    },

    finalizarPagamento() {
      this.$router.push({
        name: "matriculas.mensalidades.formaPagamento",
        params: { matricula_id: this.matriculaId, parcelaId: this.parcela.id },
      });
    },
  },
};
</script>
